import { useAuthStore } from '../stores/auth.js'
import { useSettingsStore } from '../stores/settings.js'
import { hasFeature } from '@/brand'

export function setupRouterGuards(router) {
  // Global before guard
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()
    const settingsStore = useSettingsStore()
    
    // Initialize auth store if not already done
    if (!authStore.isAuthenticated && !authStore.isLoading) {
      await authStore.initAuth()
    }

    // Fetch maintenance status on first navigation
    if (!settingsStore.initialized) {
      await settingsStore.fetchMaintenanceStatus()
    }

    // Set page title
    if (to.meta.title) {
      document.title = to.meta.title
    }

    // Maintenance mode: redirect non-admin users to maintenance page
    // Allow: /maintenance itself, /admin/* routes, /auth/* routes, /user/* if the brand keeps accounts open
    if (settingsStore.maintenanceMode && to.name !== 'maintenance') {
      const isAdminRoute = to.path.startsWith('/admin')
      const isAuthRoute = to.path.startsWith('/auth')
      const isAccountRoute = hasFeature('maintenanceAccounts') && (to.path === '/user' || to.path.startsWith('/user/'))

      if (!isAdminRoute && !isAuthRoute && !isAccountRoute) {
        // Allow authenticated admins to pass through
        if (!authStore.isAdmin) {
          next({ name: 'maintenance' })
          return
        }
      }
    }

    // If maintenance is off and user is on maintenance page, redirect away
    if (!settingsStore.maintenanceMode && to.name === 'maintenance') {
      next({ name: 'home' })
      return
    }

    // Orders only for registered customers (admin-v2 Nastavenia): guests sign up before the checkout,
    // the cart stays open to them
    const CHECKOUT_STEPS = ['checkout.details', 'checkout.personal-info', 'checkout.shipping-payment', 'checkout.review']
    if (settingsStore.ordersRegisteredOnly && !authStore.isAuthenticated && CHECKOUT_STEPS.includes(to.name)) {
      next({ name: 'auth.register', query: { redirect: to.fullPath, reason: 'checkout' } })
      return
    }

    // Check if route requires authentication
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next({
        name: 'auth.login',
        query: { redirect: to.fullPath }
      })
      return
    }

    // Check if route requires guest (not authenticated)
    // Exception: Allow authenticated users to access register page with type=b2b for account upgrade
    if (to.meta.requiresGuest && authStore.isAuthenticated) {
      if (to.name === 'auth.register' && to.query.type === 'b2b') {
        // Allow authenticated users to access B2B registration for account upgrade
        next()
        return
      }
      next({ name: 'home' })
      return
    }

    // Check if route requires admin privileges
    if (to.meta.requiresAdmin && !authStore.isAdmin) {
      next({ name: 'home' })
      return
    }

    // Check if route requires specific roles
    if (to.meta.requiresRole && Array.isArray(to.meta.requiresRole)) {
      const userRole = authStore.user?.role
      if (!userRole || !to.meta.requiresRole.includes(userRole)) {
        next({ name: 'user.dashboard' })
        return
      }
    }

    // Check if user is trying to access admin routes without admin role
    if (to.path.startsWith('/admin') && !authStore.isAdmin) {
      next({ name: 'home' })
      return
    }

    next()
  })

  // Global after guard
  router.afterEach((to, from) => {
    // Scroll to top on route change (not when a page rewrites its own URL, e.g. the guide chat)
    if (!(to.meta.keepScroll && to.name === from.name)) {
      window.scrollTo(0, 0)
    }
    
    // Close mobile menus if open
    const event = new CustomEvent('route-changed', { detail: { to, from } })
    window.dispatchEvent(event)
  })
}

// Navigation helper functions
export function redirectToLogin(router, redirectPath = null) {
  router.push({
    name: 'auth.login',
    query: redirectPath ? { redirect: redirectPath } : {}
  })
}

export function redirectAfterLogin(router, route) {
  const redirectPath = route.query.redirect
  if (redirectPath) {
    router.push(redirectPath)
  } else {
    // Redirect based on user role
    const authStore = useAuthStore()
    if (authStore.isAdmin) {
      router.push({ name: 'admin.dashboard' })
    } else {
      router.push({ name: 'user.dashboard' })
    }
  }
}

export function requiresAuth(to, from, next) {
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) {
    next({
      name: 'auth.login',
      query: { redirect: to.fullPath }
    })
  } else {
    next()
  }
}

export function requiresGuest(to, from, next) {
  const authStore = useAuthStore()
  if (authStore.isAuthenticated) {
    next({ name: 'home' })
  } else {
    next()
  }
}

export function requiresAdmin(to, from, next) {
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) {
    next({
      name: 'auth.login',
      query: { redirect: to.fullPath }
    })
  } else if (!authStore.isAdmin) {
    next({ name: 'home' })
  } else {
    next()
  }
}
